import { Grid, TextField } from "@mui/material";
import {
  AlertCircle,
  Building,
  CheckCircle,
  Edit3,
  FileText,
  Globe,
  Hash,
  Mail,
  MapPin,
  Save,
  Upload,
  X,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import "react-phone-number-input/style.css";
import authService, { fileToBase64 } from "../../api/authService";
import Button from "../../components/common/Button";
import CountrySelect from "../../components/common/CountrySelect";
import PhoneInputField from "../../components/common/PhoneInputField";
import Select from "../../components/common/Select";
import { RequiredAsterisk } from "../../components/professional/RequiredAsterisk";
import { useAuthStore } from "../../store/authStore.js";
import { organizationTypes } from "./components/dropDownData";
import { getFilenameFromUrl } from "../../utils/helpers.js";

const CompanyProfilePage = () => {
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const [formData, setFormData] = useState(null);
  const [originalData, setOriginalData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [notification, setNotification] = useState({ message: "", type: "" });
  const [profilePicture, setProfilePicture] = useState(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [certificateOfIncorporation, setCertificateOfIncorporation] =
    useState(null);
  const [regulatoryBodyCertificate, setRegulatoryBodyCertificate] =
    useState(null);
  const [docErrors, setDocErrors] = useState({});

  // Add alongside the other document states
  const [removedDocs, setRemovedDocs] = useState({
    certificate_of_incorporation: false,
    regulatory_body_certificate: false,
  });

  const handleRemoveExistingDoc = (fieldKey) => {
    setRemovedDocs((prev) => ({ ...prev, [fieldKey]: true }));
    // Clear any errors for this field
    setErrors((prev) => {
      const updated = { ...prev };
      delete updated[fieldKey];
      return updated;
    });
  };

  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

  const validateAndSetFile = (file, fieldKey, setter) => {
    if (!file) return;

    const newDocErrors = { ...docErrors };

    if (file.type !== "application/pdf") {
      newDocErrors[fieldKey] = "Only PDF files are allowed.";
      setDocErrors(newDocErrors);
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      newDocErrors[fieldKey] = "File size must not exceed 10MB.";
      setDocErrors(newDocErrors);
      return;
    }

    delete newDocErrors[fieldKey];
    setDocErrors(newDocErrors);
    setter(file);
  };

  const handleCertificateOfIncorporationSelect = (e) => {
    const file = e.target.files[0];
    validateAndSetFile(
      file,
      "certificate_of_incorporation",
      setCertificateOfIncorporation,
    );
  };

  const handleRegulatoryBodyCertificateSelect = (e) => {
    const file = e.target.files[0];
    validateAndSetFile(
      file,
      "regulatory_body_certificate",
      setRegulatoryBodyCertificate,
    );
  };
  const backendUrl =
    import.meta.env.VITE_BACKEND_DOMAIN || "http://localhost:8000";
  const fullLogoUrl = profilePicturePreview
    ? backendUrl + profilePicturePreview
    : null;

  const getLocalCompanyData = () => {
    try {
      const localData = localStorage.getItem(`companyProfile_${user?.id}`);
      return localData ? JSON.parse(localData) : {};
    } catch (error) {
      return {};
    }
  };

  const fetchProfile = useCallback(async () => {
    if (!user) return;
    setIsLoading(true);
    try {
      const { data } = await authService.getRecruiterProfile();
      const localData = getLocalCompanyData();

      //   const initializedData = {
      //     ...data,
      //     company_name: data.company_name || "",
      //     company_website: data.company_website || "",
      //     company_description: localData.company_description || "",
      //     company_type: localData.company_type || "",
      //     company_size: localData.company_size || "",
      //     company_address: localData.company_address || "",
      //     company_phone: localData.company_phone || "",
      //   };
      setFormData({
        ...data,
        company_email: data.company_email || data.email || "",
      });
      setOriginalData(data);
      setProfilePicturePreview(
        data.company_logo || data.profile_picture || null,
      );
    } catch (error) {
      showNotification("Could not load company profile data.", "error");
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const showNotification = (message, type) => {
    setNotification({ message, type });
    setTimeout(() => setNotification({ message: "", type: "" }), 4000);
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      const newErrors = { ...errors };
      delete newErrors[field];
      setErrors(newErrors);
    }
  };

  const handleCancel = () => {
    setFormData(originalData);
    setProfilePicture(null);
    setProfilePicturePreview(
      originalData.company_logo || originalData.profile_picture || null,
    );
    setCertificateOfIncorporation(null);
    setRegulatoryBodyCertificate(null);
    setRemovedDocs({
      certificate_of_incorporation: false,
      regulatory_body_certificate: false,
    });
    setErrors({});
    setIsEditing(false);
  };

  const validateForm = () => {
    const errors = {};
    let valid = true;

    if (!formData.company_name?.trim()) {
      errors.company_name = "Organization name is required.";
      valid = false;
    }
    if (!formData.country?.trim()) {
      errors.country = "Country is required.";
      valid = false;
    }
    if (!formData.company_email?.trim()) {
      errors.company_email = "Company email is required.";
      valid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.company_email)) {
      errors.company_email = "Invalid email format.";
      valid = false;
    }
    if (!formData.company_phone?.trim()) {
      errors.company_phone = "Company phone is required.";
      valid = false;
    }
    if (!formData.company_type?.trim()) {
      errors.company_type = "Company type is required.";
      valid = false;
    }
    if (formData.company_type === "other" && !formData.other_company_type?.trim()) {
      errors.other_company_type = "Please specify your company type.";
      valid = false;
    }
    if (!/^https?:\/\/[^\s$.?#].[^\s]*$/.test(formData.company_website)) {
      errors.company_website = "Invalid website URL.";
      valid = false;
    }

    // ─── Document validation ───
    if (
      (!formData.certificate_of_incorporation && !certificateOfIncorporation) ||
      (removedDocs.certificate_of_incorporation && !certificateOfIncorporation)
    ) {
      errors.certificate_of_incorporation =
        "Certificate of Incorporation is required.";
      valid = false;
    }

    if (
      (!formData.regulatory_body_certificate && !regulatoryBodyCertificate) ||
      (removedDocs.regulatory_body_certificate && !regulatoryBodyCertificate)
    ) {
      errors.regulatory_body_certificate =
        "Regulatory Body Certificate is required.";
      valid = false;
    }

    const allErrors = { ...errors, ...docErrors };
    setErrors(allErrors);
    return valid && Object.keys(docErrors).length === 0;
  };

  const handleSave = async () => {
    setIsSaving(true);

    const apiPayload = {
      company_name: formData.company_name,
      company_website: formData.company_website,
      company_description: formData.company_description,
      company_type: formData.company_type,
      other_company_type: formData.company_type === "other" ? formData.other_company_type : "",
      company_address: formData.company_address,
      company_phone: formData.company_phone,
      company_email: formData.company_email,
      country: formData.country,
      company_registration_number: formData.company_registration_number,
      company_about: formData.company_about,
    };

    const localPayload = {
      company_description: formData.company_description,
      company_industry: formData.company_industry,
      company_size: formData.company_size,
      company_address: formData.company_address,
      company_phone: formData.company_phone,
    };

    if (profilePicture) {
      // apiPayload.profile_picture = profilePicture;
      if (profilePicture instanceof File) {
        apiPayload.company_logo = await fileToBase64(profilePicture);
      }
      // apiPayload.company_logo = profilePicture;
    }

    // ✅ Convert new files first
if (certificateOfIncorporation instanceof File) {
  apiPayload.certificate_of_incorporation = await fileToBase64(certificateOfIncorporation);
} else if (removedDocs.certificate_of_incorporation) {
  // Only send null if no replacement was provided
  apiPayload.certificate_of_incorporation = null;
}

if (regulatoryBodyCertificate instanceof File) {
  apiPayload.regulatory_body_certificate = await fileToBase64(regulatoryBodyCertificate);
} else if (removedDocs.regulatory_body_certificate) {
  apiPayload.regulatory_body_certificate = null;
}

    setRemovedDocs({
      certificate_of_incorporation: false,
      regulatory_body_certificate: false,
    });

    try {
      const { data } = await authService.updateRecruiterProfile(apiPayload);
      localStorage.setItem(
        `companyProfile_${user.id}`,
        JSON.stringify(localPayload),
      );

      const updatedData = { ...formData, ...data };
      setFormData(updatedData);
      setOriginalData(updatedData);

      const updatedUser = { ...user, ...data };
      setUser(updatedUser);
      localStorage.setItem("winguport_user", JSON.stringify(updatedUser));

      setProfilePicture(null);
      setProfilePicturePreview(data.company_logo || data.profile_picture);
      setCertificateOfIncorporation(null);
      setRegulatoryBodyCertificate(null);
      setIsEditing(false);
      showNotification("Profile updated successfully!", "success");
    } catch (error) {
      showNotification("Failed to save changes. Please try again.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) handleSave();
  };
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfilePicture(file);
      setProfilePicturePreview(URL.createObjectURL(file));
    }
  };

  

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-secondary-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!formData) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-secondary-50">
        <div className="text-center py-20 text-red-600 font-semibold">
          Could not load profile. Please try refreshing the page.
        </div>
      </div>
    );
  }

  return (
    <div className="bg-secondary-50 min-h-screen">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {notification.message && (
          <div
            className={`fixed top-24 right-4 z-50 p-4 rounded-lg shadow-lg border flex items-center transition-all duration-300 ${
              notification.type === "success"
                ? "bg-green-50 border-green-200 text-green-800"
                : "bg-red-50 border-red-200 text-red-800"
            }`}
          >
            {notification.type === "success" ? (
              <CheckCircle className="w-5 h-5 mr-3 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-5 h-5 mr-3 flex-shrink-0" />
            )}
            <span className="text-sm font-medium">{notification.message}</span>
          </div>
        )}

        <div className="flex flex-col sm:flex-row justify-between sm:items-center mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-secondary-900">
              Organization Profile
            </h1>
            <p className="text-secondary-600 mt-1">
              Manage your organization information and details
            </p>
          </div>
          {!isEditing && (
            <Button
              variant="primary"
              onClick={() => setIsEditing(true)}
              className="w-full sm:w-auto"
            >
              <Edit3 className="w-4 h-4 mr-2" />
              Edit Profile
            </Button>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          {/* Profile Header Section */}
          <div className="p-6 sm:p-8 bg-gradient-to-r from-primary-50 to-secondary-50 border-b border-secondary-200">
            <div className="flex flex-col sm:flex-row items-center gap-6">
              <div className="relative flex-shrink-0">
                <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full overflow-hidden border-4 border-white shadow-lg bg-white">
                  <img
                    src={
                      fullLogoUrl ||
                      `https://placehold.co/128x128/E2E8F0/475569?text=${
                        formData.company_name?.charAt(0) || "C"
                      }`
                    }
                    alt="Company Logo"
                    className="w-full h-full object-cover"
                  />
                </div>
                {isEditing && (
                  <>
                    <input
                      type="file"
                      id="profile-picture-upload"
                      className="hidden"
                      onChange={handleFileSelect}
                      accept="image/*"
                    />
                    <label
                      htmlFor="profile-picture-upload"
                      className="absolute bottom-0 right-0 cursor-pointer bg-primary-600 text-white p-2 rounded-full hover:bg-primary-700 transition-colors shadow-lg"
                    >
                      <Upload className="w-4 h-4" />
                    </label>
                  </>
                )}
              </div>

              <div className="text-center sm:text-left flex-1">
                {isEditing ? (
                  <>
                    <input
                      value={formData.company_name || ""}
                      onChange={(e) =>
                        handleInputChange("company_name", e.target.value)
                      }
                      className={`text-2xl sm:text-3xl font-bold text-secondary-900 w-full p-3 border rounded-lg bg-white focus:ring-2 ${
                        errors.company_name
                          ? "border-red-500 focus:ring-red-500"
                          : "border-secondary-300 focus:ring-primary-500"
                      } focus:border-transparent`}
                      placeholder="Company Name"
                    />
                    {errors.company_name && (
                      <p className="mt-1 text-xs text-red-600">
                        {errors.company_name}
                      </p>
                    )}
                  </>
                ) : (
                  <h2 className="text-2xl sm:text-3xl font-bold text-secondary-900">
                    {formData.company_name || "Company Name"}
                  </h2>
                )}

                <div className="mt-2">
                  {isEditing ? (
                    <>
                      <input
                        value={formData.company_website || ""}
                        onChange={(e) =>
                          handleInputChange("company_website", e.target.value)
                        }
                        className="text-secondary-600 w-full p-2 border border-secondary-300 rounded-lg bg-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="https://example.com"
                      />
                      {errors.company_website && (
                        <p className="mt-1 text-xs text-red-600">
                          {errors.company_website}
                        </p>
                      )}
                    </>
                  ) : (
                    <div className="flex items-center justify-center sm:justify-start space-x-2">
                      <Globe className="w-4 h-4 text-primary-600" />
                      {formData.company_website ? (
                        <a
                          href={formData.company_website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600 hover:underline font-medium"
                        >
                          {formData.company_website}
                        </a>
                      ) : (
                        <span className="text-secondary-500">
                          No website provided
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="p-6 sm:p-8 space-y-8">
            {/* About Company Section */}
            <Section title="About Company">
              {isEditing ? (
                <textarea
                  value={formData.company_about || ""}
                  onChange={(e) =>
                    handleInputChange("company_about", e.target.value)
                  }
                  rows="5"
                  className="w-full p-3 border border-secondary-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none outline-none ring-2 ring-primary-500 ring-offset-2"
                  placeholder="Tell us about your company, mission, vision, and what makes you unique..."
                />
              ) : (
                <div className="bg-secondary-50 p-4 rounded-lg">
                  <p className="text-secondary-700 whitespace-pre-line leading-relaxed">
                    {formData.company_about ||
                      "No description provided. Add a compelling description to attract top talent!"}
                  </p>
                </div>
              )}
            </Section>

            {/* Company Details Section */}
            <Section title="Company Details">
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label={
                      <span>
                        Company Email <RequiredAsterisk />
                      </span>
                    }
                    value={formData.company_email || formData.email}
                    onChange={(e) =>
                      handleInputChange("company_email", e.target.value)
                    }
                    error={!!errors.company_email}
                    helperText={errors.company_email}
                    disabled={!isEditing}
                    // required
                    slotProps={{
                      input: {
                        startAdornment: (
                          <Mail className="w-4 h-4 text-gray-400 mr-2" />
                        ),
                      },
                    }}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <PhoneInputField
                    errors={errors}
                    onChange={(value) =>
                      handleInputChange("company_phone", value)
                    }
                    value={formData.company_phone}
                    disabled={!isEditing}
                    containerStyle={{
                      height: "55px",
                      borderRadius: "10px",
                    }}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <CountrySelect
                    value={formData.country}
                    id="country"
                    onChange={(value) => handleInputChange("country", value)}
                    error={!!errors.country}
                    helperText={errors.country}
                    required
                    disabled={!isEditing}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label={<span>Registration Number</span>}
                    value={formData.company_registration_number}
                    onChange={(e) =>
                      handleInputChange(
                        "company_registration_number",
                        e.target.value,
                      )
                    }
                    error={!!errors.company_registration_number}
                    helperText={errors.company_registration_number}
                    disabled={!isEditing}
                    // required
                    slotProps={{
                      input: {
                        startAdornment: (
                          <Hash className="w-4 h-4 text-gray-400 mr-2" />
                        ),
                      },
                    }}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  {!isEditing ? (
                    <TextField
                      fullWidth
                      label={
                        <span>
                          Company Type <RequiredAsterisk />
                        </span>
                      }
                      value={formData.company_type}
                      onChange={(e) =>
                        handleInputChange("company_type", e.target.value)
                      }
                      error={!!errors.company_type}
                      helperText={errors.company_type}
                      disabled={!isEditing}
                      // required
                      slotProps={{
                        input: {
                          startAdornment: (
                            <Building className="w-4 h-4 text-gray-400 mr-2" />
                          ),
                        },
                      }}
                    />
                  ) : (
                    <Select
                      error={!!errors.company_type}
                      helperText={errors.company_type}
                      label={
                        <span>
                          Company Type
                          <RequiredAsterisk />
                        </span>
                      }
                      onChange={(value) =>
                        handleInputChange("company_type", value)
                      }
                      options={organizationTypes}
                      value={formData.company_type}
                    />
                  )}
                </Grid>

                {formData.company_type === "other" && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label={
                        <span>
                          Specify Company Type <RequiredAsterisk />
                        </span>
                      }
                      value={formData.other_company_type || ""}
                      onChange={(e) =>
                        handleInputChange("other_company_type", e.target.value)
                      }
                      error={!!errors.other_company_type}
                      helperText={errors.other_company_type}
                      disabled={!isEditing}
                      placeholder="e.g., Aviation Consulting, Ground Handling"
                      slotProps={{
                        input: {
                          startAdornment: (
                            <Building className="w-4 h-4 text-gray-400 mr-2" />
                          ),
                        },
                      }}
                    />
                  </Grid>
                )}

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label={<span>Company Address</span>}
                    value={formData.company_address}
                    onChange={(e) =>
                      handleInputChange("company_address", e.target.value)
                    }
                    error={!!errors.company_address}
                    helperText={errors.company_address}
                    disabled={!isEditing}
                    // required
                    slotProps={{
                      input: {
                        startAdornment: (
                          <MapPin className="w-4 h-4 text-gray-400 mr-2" />
                        ),
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Section>

            <Section title="Documents">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <DocumentUploadItem
                  label="Certificate of Incorporation"
                  icon={FileText}
                  isEditing={isEditing}
                  existingFileUrl={formData.certificate_of_incorporation}
                  selectedFile={certificateOfIncorporation}
                  isRemoved={removedDocs.certificate_of_incorporation}
                  onFileSelect={handleCertificateOfIncorporationSelect}
                  onRemoveExisting={() =>
                    handleRemoveExistingDoc("certificate_of_incorporation")
                  }
                  onRemoveSelected={() => setCertificateOfIncorporation(null)}
                  error={errors.certificate_of_incorporation}
                  inputId="certificate-of-incorporation-upload"
                  backendUrl={backendUrl}
                  required
                />

                <DocumentUploadItem
                  label="Regulatory Body Certificate"
                  icon={FileText}
                  isEditing={isEditing}
                  existingFileUrl={formData.regulatory_body_certificate}
                  selectedFile={regulatoryBodyCertificate}
                  isRemoved={removedDocs.regulatory_body_certificate}
                  onFileSelect={handleRegulatoryBodyCertificateSelect}
                  onRemoveExisting={() =>
                    handleRemoveExistingDoc("regulatory_body_certificate")
                  }
                  onRemoveSelected={() => setRegulatoryBodyCertificate(null)}
                  error={errors.regulatory_body_certificate}
                  inputId="regulatory-body-certificate-upload"
                  backendUrl={backendUrl}
                  required
                />
              </div>
            </Section>

            {/* Action Buttons */}
            {isEditing && (
              <div className="flex flex-col-reverse sm:flex-row justify-end space-y-reverse space-y-2 sm:space-y-0 sm:space-x-3 pt-6 border-t border-secondary-200">
                <Button
                  variant="secondary"
                  onClick={handleCancel}
                  className="w-full sm:w-auto"
                  disabled={isSaving}
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmit}
                  loading={isSaving}
                  className="w-full sm:w-auto mb-2 sm:mb-0"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div className="space-y-4">
    <h3 className="text-xl font-bold text-secondary-800 flex items-center">
      {title}
    </h3>
    {children}
  </div>
);

const DetailItem = ({
  icon: Icon,
  label,
  value,
  isEditing,
  onChange,
  type = "text",
  placeholder,
  error = null,
  required = false,
}) => (
  <div className="space-y-2">
    <label className="text-sm font-semibold text-secondary-700 flex items-center">
      {Icon && <Icon className="w-4 h-4 mr-2 text-secondary-500" />}
      {label}&nbsp;{required && <RequiredAsterisk />}
    </label>

    {isEditing ? (
      <div>
        {type === "phone" ? (
          <PhoneInputField
            errors={error}
            onChange={onChange}
            value={value}
            containerStyle={{
              height: "45px",
              borderRadius: "10px",
            }}
          />
        ) : (
          // <PhoneInput
          //   international
          //   defaultCountry="KE"
          //   value={value}
          //   onChange={onChange}
          //   className={`phone-input-custom w-full p-3 border rounded-lg focus:ring-2 focus:border-transparent text-sm
          //     ${
          //       error
          //         ? "border-red-500 focus:ring-red-500"
          //         : "border-secondary-300 focus:ring-primary-500"
          //     }`}
          // />
          <input
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            className={`w-full p-3 border rounded-lg focus:ring-2 focus:border-transparent text-sm
              ${
                error
                  ? "border-red-500 focus:ring-red-500"
                  : "border-secondary-300 focus:ring-primary-500"
              }`}
            placeholder={placeholder}
          />
        )}
        {error && type !== "phone" && (
          <p className="mt-1 text-xs text-red-600">{error}</p>
        )}
        {error && type === "phone" && (
          <p className="mt-1 text-xs text-red-600">{error.company_phone}</p>
        )}
      </div>
    ) : (
      <div className="bg-secondary-50 p-3 rounded-lg border border-secondary-100">
        <p className="font-medium text-secondary-800 text-sm">
          {value || <span className="text-secondary-400 italic">Not set</span>}
        </p>
      </div>
    )}
  </div>
);

const DocumentUploadItem = ({
  label,
  icon: Icon,
  isEditing,
  existingFileUrl,
  selectedFile,
  isRemoved, // ← new
  onFileSelect,
  onRemoveExisting, // ← new
  onRemoveSelected,
  error,
  inputId,
  backendUrl,
  required = false,
}) => {
  const hasExisting = !!existingFileUrl && !isRemoved;
  const hasNewFile = !!selectedFile;

  const fullExistingUrl = existingFileUrl
    ? existingFileUrl.startsWith("http")
      ? existingFileUrl
      : backendUrl + existingFileUrl
    : null;

  return (
    <div className="space-y-2">
      <label className="text-sm font-semibold text-secondary-700 flex items-center">
        {Icon && <Icon className="w-4 h-4 mr-2 text-secondary-500" />}
        {label}&nbsp;{required && <RequiredAsterisk />}
      </label>

      <div
        className={`p-4 rounded-lg border ${
          error
            ? "border-red-500 bg-red-50"
            : "border-secondary-200 bg-secondary-50"
        }`}
      >
        {hasNewFile ? (
          // New file selected — pending upload
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 overflow-hidden">
              <FileText className="w-4 h-4 text-primary-600 flex-shrink-0" />
              <span className="text-sm text-secondary-800 truncate">
                {selectedFile.name}
              </span>
              <span className="text-xs text-secondary-500 flex-shrink-0">
                ({(selectedFile.size / (1024 * 1024)).toFixed(2)} MB)
              </span>
            </div>
            {isEditing && (
              <button
                type="button"
                onClick={onRemoveSelected}
                className="text-red-500 hover:text-red-700 flex-shrink-0 ml-2"
                title="Remove"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        ) : hasExisting ? (
          // Existing uploaded document
          <div className="flex items-center justify-between">
            <a
              href={fullExistingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 text-primary-600 hover:underline overflow-hidden"
            >
              <FileText className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm truncate">
                {getFilenameFromUrl(existingFileUrl) || "View uploaded document"}</span>
            </a>
            {isEditing && (
              <button
                type="button"
                onClick={onRemoveExisting}
                className="text-red-500 hover:text-red-700 flex-shrink-0 ml-2"
                title="Remove document"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        ) : isRemoved ? (
          // Marked for removal — show warning
          <div className="flex items-center space-x-2 text-amber-600 text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>
              Document will be removed on save. Upload a replacement below.
            </span>
          </div>
        ) : (
          // Nothing uploaded yet
          <div className="flex items-center space-x-2 text-secondary-400 italic text-sm">
            <FileText className="w-4 h-4" />
            <span>Not uploaded</span>
          </div>
        )}

        {isEditing && (
          <div className="mt-3">
            <input
              type="file"
              id={inputId}
              className="hidden"
              accept="application/pdf"
              onChange={onFileSelect}
            />
            <label
              htmlFor={inputId}
              className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-lg cursor-pointer bg-white border border-secondary-300 text-secondary-700 hover:bg-secondary-100 transition-colors"
            >
              <Upload className="w-4 h-4 mr-2" />
              {hasExisting
                ? "Replace PDF"
                : hasNewFile
                  ? "Change PDF"
                  : "Upload PDF"}
            </label>
            <p className="mt-1 text-xs text-secondary-500">
              PDF only, max 10MB.
            </p>
          </div>
        )}
      </div>

      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
};

export default CompanyProfilePage;
